import React from "react";
import { Autocomplete, Box, TextField } from "@mui/material";
import { MRT_Row, MRT_ColumnDef } from "material-react-table";
import type { Job, Deal } from "../../../../../../models";
import { getValidationErrorKey } from "../../../../../../utilities/jobValidation";
import { ColumnContext } from "../standard-column-types";
import { columnDisplayNames } from "../../../../../admin/ImportSettings";
import { useDealColumnViewModel } from "./Deal.column.vm";
import { DealColumnViewModel, DealOption } from "./Deal.column.types";
import { getDealOptionLabel } from "./Deal.column.helpers";
import { useValidationErrors } from "../../shared/hooks/ValidationErrorsContext";
import { useIsRowInEditMode, useEditModeContext } from "../../shared/hooks/EditModeContext";

interface DealColumnCellProps {
	row: MRT_Row<Job>;
	vm: DealColumnViewModel;
	allDeals: Deal[];
}

/**
 * Memoized Cell component for Deal column
 * Prevents unnecessary re-renders when column definitions change
 */
const DealColumnCell = React.memo(
	function DealColumnCell({ row, vm, allDeals }: DealColumnCellProps) {
		const job = row.original;
		const validationErrors = useValidationErrors();
		const isInEditMode = useIsRowInEditMode(job.id);
		const { exitRowEdit } = useEditModeContext();
		const currentDeal = vm.getCurrentDeal(job);
		const isRequired = vm.getIsRequired(row);
		const errorKey = getValidationErrorKey("deal", job.id);
		const hasError = !!validationErrors?.[errorKey];

		// View Mode: render deal label for performance
		if (!isInEditMode) {
			const displayLabel = currentDeal ? getDealOptionLabel(currentDeal) : "";
			return (
				<Box
					title={hasError ? validationErrors[errorKey] : displayLabel}
					sx={{
						overflow: "hidden",
						textOverflow: "ellipsis",
						whiteSpace: "nowrap",
						color: hasError ? "error.main" : "inherit",
						borderBottom: hasError ? "2px solid" : "none",
						borderColor: "error.main",
						width: "100%",
						px: 0.5,
					}}
				>
					{displayLabel || <span style={{ color: "#999" }}>-</span>}
				</Box>
			);
		}

		return (
			<Autocomplete
				fullWidth={true}
				size="small"
				value={currentDeal}
				options={[...allDeals, { id: "custom" }] as DealOption[]}
				isOptionEqualToValue={(option, value) => option.id === value.id}
				getOptionKey={(option) => option.id}
				renderInput={(params) => (
					<TextField
						error={hasError}
						helperText={validationErrors?.[errorKey]}
						required={isRequired}
						{...params}
						onKeyDown={(e) => {
							if (e.key === "Escape") {
								exitRowEdit(job.id);
							}
						}}
					/>
				)}
				onChange={(_event, newDeal) => {
					vm.handleDealChange(job.id, newDeal, row);
				}}
				getOptionLabel={getDealOptionLabel}
			/>
		);
	},
	(prevProps, nextProps) => {
		// Custom comparison for better memoization
		const prevJob = prevProps.row.original;
		const nextJob = nextProps.row.original;

		return (
			prevJob.id === nextJob.id &&
			prevJob.dealIds?.[0] === nextJob.dealIds?.[0] &&
			prevProps.allDeals === nextProps.allDeals
		);
	},
);

interface DealColumnAggregatedCellProps {
	row: MRT_Row<Job>;
	vm: DealColumnViewModel;
	allDeals: Deal[];
}

/**
 * Memoized AggregatedCell component for Deal column
 * Renders editable Autocomplete when group row is in edit mode
 */
const DealColumnAggregatedCell = React.memo(
	function DealColumnAggregatedCell({ row, vm, allDeals }: DealColumnAggregatedCellProps) {
		// For group rows, check edit mode using row.id (e.g., "grower:Alexander, Curt")
		const isInEditMode = useIsRowInEditMode(row.id);
		const { exitRowEdit } = useEditModeContext();

		// Get value from first job in group
		const job = row.original;
		const currentDeal = vm.getCurrentDeal(job);

		// View Mode: render deal label
		if (!isInEditMode) {
			const displayLabel = currentDeal && currentDeal.id !== "custom" ? getDealOptionLabel(currentDeal) : "";
			return (
				<Box
					title={displayLabel || undefined}
					sx={{
						overflow: "hidden",
						textOverflow: "ellipsis",
						whiteSpace: "nowrap",
						width: "100%",
						px: 0.5,
					}}
				>
					{displayLabel || <span style={{ color: "#999" }}>-</span>}
				</Box>
			);
		}

		// Edit Mode: render Autocomplete that updates all rows in group
		return (
			<Autocomplete
				fullWidth={true}
				size="small"
				value={currentDeal}
				options={[...allDeals, { id: "custom" }] as DealOption[]}
				isOptionEqualToValue={(option, value) => option.id === value.id}
				getOptionKey={(option) => option.id}
				renderInput={(params) => (
					<TextField
						{...params}
						onKeyDown={(e) => {
							if (e.key === "Escape") {
								exitRowEdit(row.id);
							}
						}}
					/>
				)}
				onChange={(_event, newDeal) => {
					vm.handleGroupDealChange(row, newDeal);
				}}
				getOptionLabel={getDealOptionLabel}
			/>
		);
	},
	(prevProps, nextProps) => {
		const prevJob = prevProps.row.original;
		const nextJob = nextProps.row.original;

		return (
			prevJob.id === nextJob.id &&
			prevJob.dealIds?.[0] === nextJob.dealIds?.[0] &&
			prevProps.allDeals === nextProps.allDeals
		);
	},
);

/**
 * Build the Deal column
 * Uses memoized Cell components to prevent unnecessary re-renders when column definitions change
 */
export function column_Deal(context: ColumnContext): MRT_ColumnDef<Job, unknown> {
	const vm = useDealColumnViewModel(context);

	return {
		id: "deal",
		header: columnDisplayNames.deal,
		accessorKey: "deal",
		enableEditing: false,
		size: 400,
		Cell: ({ row }: { row: MRT_Row<Job> }) => <DealColumnCell row={row} vm={vm} allDeals={context.props.allDeals} />,
		AggregatedCell: ({ row }: { row: MRT_Row<Job> }) => (
			<DealColumnAggregatedCell row={row} vm={vm} allDeals={context.props.allDeals} />
		),
	};
}
